import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Hammer, ClipboardList, FileText, LogOut, CheckCircle, Clock, XCircle,
  ChevronRight, Building2, AlertCircle, MessageSquare, Send, Paperclip,
  ChevronDown, DollarSign, Upload, Eye, Calendar, User, X, ShieldCheck,
  Settings as SettingsIcon, Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import { toast } from '../components/ui/sonner';
import axios from 'axios';
import { RichTextEditor } from '../components/RichTextEditor';
import ContractorISG from '../components/ContractorISG';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'Yeni Talep', color: 'bg-blue-100 text-blue-700', icon: Clock },
  viewed: { label: 'İncelendi', color: 'bg-purple-100 text-purple-700', icon: Eye },
  bid_submitted: { label: 'Teklif Verildi', color: 'bg-amber-100 text-amber-700', icon: FileText },
  awarded: { label: 'Kazanıldı!', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  not_awarded: { label: 'Kazanılamadı', color: 'bg-gray-100 text-gray-500', icon: XCircle },
  rejected: { label: 'Reddedildi', color: 'bg-red-100 text-red-600', icon: XCircle },
};

const BID_CURRENCY_OPTIONS = [
  { value: 'usd', label: 'USD – US Dollar' },
  { value: 'eur', label: 'EUR – Euro' },
  { value: 'try', label: 'TRY – Türk Lirası' },
  { value: 'gbp', label: 'GBP – Pound Sterling' },
];

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-600', icon: Clock };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
}

// Phase 86AZ.40 — Direct price offer card on contractor dashboard.
// Anonymized (no customer/tenant identity shown). Accept/Reject only.
function DirectOfferCard({ offer, tr, onResponded }) {
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState('');
  const [showRejectNote, setShowRejectNote] = useState(false);
  // Phase 86AZ.44 — Lightbox state for file preview
  const [previewIdx, setPreviewIdx] = useState(-1);
  const respond = async (accept) => {
    if (!accept && note.trim().length === 0) {
      // Soft prompt: encourage a reason on reject but don't block
    }
    if (!window.confirm(accept ? (tr ? 'Bu teklifi KABUL ediyor musunuz?' : 'Accept this offer?')
                                : (tr ? 'Bu teklifi REDDETMEK istediğinize emin misiniz?' : 'Reject this offer?'))) return;
    setSubmitting(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const token = localStorage.getItem('cop31_contractor_token');
      await axios.post(`${API}/stand-designs/contractor/direct-offers/${offer.id}/respond`,
        { accept, note: note || null },
        { headers: { Authorization: `Bearer ${token}` } });
      onResponded?.();
    } catch (e) {
      alert(e?.response?.data?.detail || (tr ? 'İşlem başarısız.' : 'Action failed.'));
    } finally { setSubmitting(false); }
  };
  const isPending = offer.status === 'pending';
  const statusPill = (() => {
    const map = {
      pending:   { txt: tr ? 'Yanıt Bekliyor' : 'Pending',   cls: 'bg-amber-100 text-amber-700' },
      accepted:  { txt: tr ? 'Kabul Ettiniz' : 'You accepted', cls: 'bg-emerald-100 text-emerald-700' },
      rejected:  { txt: tr ? 'Reddettiniz' : 'You rejected',  cls: 'bg-red-100 text-red-700' },
      finalized: { txt: tr ? 'Onaylandı — Size Atandı' : 'Awarded to you', cls: 'bg-blue-100 text-blue-700' },
      cancelled: { txt: tr ? 'İptal Edildi' : 'Cancelled',    cls: 'bg-gray-100 text-gray-500' },
    };
    return map[offer.status] || { txt: offer.status, cls: 'bg-gray-100 text-gray-600' };
  })();
  const r = offer.request || {};
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid={`contractor-direct-offer-${offer.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="font-bold text-gray-800 text-sm truncate">{r.title || (tr ? 'Stand Üretim Talebi' : 'Stand Production Request')}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {[r.hall && `Hall: ${r.hall}`, r.stand_no && `Stand: ${r.stand_no}`, r.stand_area && `${r.stand_area} m²`].filter(Boolean).join(' · ')}
          </p>
        </div>
        <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold ${statusPill.cls}`}>{statusPill.txt}</span>
      </div>

      {/* Phase 86AZ.53 — Two-column layout: project info on the left,
          permanent chat panel on the right (matches the Ultra Admin view). */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 space-y-3 min-w-0">
          <div className="grid grid-cols-3 gap-3 p-3 rounded-xl bg-violet-50 border border-violet-100">
            <div>
              <p className="text-[10px] text-violet-600 uppercase font-bold">{tr ? 'Fiyat/m²' : 'Price/m²'}</p>
              <p className="font-mono font-bold text-violet-900 text-sm">{offer.price_per_m2} {offer.currency}</p>
            </div>
            <div>
              <p className="text-[10px] text-violet-600 uppercase font-bold">{tr ? 'Alan' : 'Area'}</p>
              <p className="font-mono text-violet-800 text-sm">{offer.area_m2} m²</p>
            </div>
            <div>
              <p className="text-[10px] text-violet-600 uppercase font-bold">{tr ? 'Toplam' : 'Total'}</p>
              <p className="font-mono font-bold text-violet-900 text-base">
                {offer.total_amount != null ? `${offer.total_amount} ${offer.currency}` : '—'}
              </p>
            </div>
          </div>
          {offer.admin_note && (
            <div className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
              <span className="font-bold">{tr ? 'Yönetici notu: ' : 'Admin note: '}</span>{offer.admin_note}
            </div>
          )}

      {/* Phase 86AZ.43 — Brief'e bağlı dosyaları contractor da görebilsin */}
      {(r.design_files?.length > 0 || r.technical_files?.length > 0) && (() => {
        const API = process.env.REACT_APP_BACKEND_URL;
        // Phase 86AZ.44 — Flatten design + technical into a single list so
        // the lightbox can navigate across both with prev/next arrows.
        const allFiles = [
          ...(r.design_files || []).map(f => ({ ...f, _bucket: 'design' })),
          ...(r.technical_files || []).map(f => ({ ...f, _bucket: 'technical' })),
        ];
        const FileTile = ({ f, color, idx }) => {
          const isPdf = (f.filename || '').endsWith('.pdf') || (f.original_name || '').endsWith('.pdf');
          return (
            <button type="button" onClick={() => setPreviewIdx(idx)}
              data-testid={`contractor-offer-file-${idx}`}
              className={`flex flex-col gap-1 p-2.5 border rounded-xl bg-white hover:shadow-md transition-all min-w-0 text-left ${color === 'orange' ? 'border-orange-200 hover:border-orange-400' : 'border-blue-200 hover:border-blue-400'}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${isPdf ? 'bg-red-100 text-red-600' : (color === 'orange' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600')}`}>
                {isPdf ? 'PDF' : 'PPT'}
              </div>
              <p className="text-xs text-gray-700 font-medium leading-tight line-clamp-2 break-all">{f.original_name}</p>
              {f.size != null && (
                <p className="text-[10px] text-gray-400">
                  {f.size < 1024*1024 ? `${(f.size/1024).toFixed(0)} KB` : `${(f.size/1024/1024).toFixed(1)} MB`}
                </p>
              )}
            </button>
          );
        };
        // Lightbox derived state
        const cur = previewIdx >= 0 ? allFiles[previewIdx] : null;
        const curUrl = cur ? `${API}/api/uploads/${cur.filename}` : null;
        const curExt = cur ? (cur.original_name || '').split('.').pop().toLowerCase() : '';
        const isImg = ['jpg','jpeg','png','webp','gif','bmp','heic','heif'].includes(curExt);
        const isPdf = curExt === 'pdf';
        return (
          <>
            <div className="mb-2 space-y-2">
              {r.design_files?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-orange-600 uppercase tracking-wide mb-1">
                    {tr ? 'Tasarım Dosyaları' : 'Design Files'} ({r.design_files.length})
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {r.design_files.map((f, i) => <FileTile key={i} f={f} color="orange" idx={i} />)}
                  </div>
                </div>
              )}
              {r.technical_files?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wide mb-1">
                    {tr ? 'Teknik Dosyalar' : 'Technical Files'} ({r.technical_files.length})
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {r.technical_files.map((f, i) =>
                      <FileTile key={i} f={f} color="blue" idx={(r.design_files?.length || 0) + i} />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Phase 86AZ.44 — Lightbox modal */}
            {cur && (
              <div
                className="fixed inset-0 z-[10000] bg-black/90 flex items-center justify-center"
                onClick={(e) => { if (e.target === e.currentTarget) setPreviewIdx(-1); }}
                data-testid="contractor-file-preview-modal"
              >
                <div className="absolute top-0 left-0 right-0 px-5 py-3 flex items-center justify-between text-white bg-gradient-to-b from-black/60 to-transparent">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-sm font-bold">{previewIdx + 1} / {allFiles.length}</span>
                    <span className="text-xs text-white/70 truncate">{cur.original_name}</span>
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${cur._bucket === 'design' ? 'bg-orange-500/30' : 'bg-blue-500/30'}`}>
                      {cur._bucket === 'design' ? (tr ? 'Tasarım' : 'Design') : (tr ? 'Teknik' : 'Technical')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <a href={curUrl} download={cur.original_name} target="_blank" rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold">
                      {tr ? 'İndir' : 'Download'}
                    </a>
                    <button type="button" onClick={() => setPreviewIdx(-1)}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/10 hover:bg-white/20">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {allFiles.length > 1 && (
                  <>
                    <button type="button"
                      onClick={(e) => { e.stopPropagation(); setPreviewIdx((previewIdx - 1 + allFiles.length) % allFiles.length); }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-2xl">
                      ‹
                    </button>
                    <button type="button"
                      onClick={(e) => { e.stopPropagation(); setPreviewIdx((previewIdx + 1) % allFiles.length); }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-2xl">
                      ›
                    </button>
                  </>
                )}
                <div className="max-w-[90vw] max-h-[85vh] w-full h-full flex items-center justify-center px-16" onClick={e => e.stopPropagation()}>
                  {isImg ? (
                    <img src={curUrl} alt={cur.original_name} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
                  ) : isPdf ? (
                    <iframe src={curUrl} title={cur.original_name} className="w-full h-full bg-white rounded-lg shadow-2xl" />
                  ) : (
                    <div className="bg-white rounded-2xl p-10 text-center max-w-md shadow-2xl">
                      <p className="font-bold text-gray-800 break-all mb-1">{cur.original_name}</p>
                      <p className="text-xs text-gray-500 uppercase font-mono mb-5">{curExt} {tr ? 'dosyası tarayıcıda önizlenemez' : 'cannot be previewed'}</p>
                      <a href={curUrl} download target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                        {tr ? 'İndir' : 'Download'}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {r.description && r.description !== '<p></p>' && (
        <details className="mb-2 text-xs">
          <summary className="cursor-pointer text-gray-500 hover:text-gray-700 font-medium">
            {tr ? 'Stand brief detayını göster' : 'Show stand brief'}
          </summary>
          <div className="mt-1.5 p-2 bg-gray-50 rounded-lg text-gray-700 leading-relaxed [&_ul]:list-disc [&_ul]:pl-4 [&_p]:my-0.5"
            dangerouslySetInnerHTML={{ __html: r.description }} />
        </details>
      )}
      {offer.contractor_response_note && (
        <p className="text-[11px] text-gray-500 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1 mb-2">
          <span className="font-bold">{tr ? 'Notunuz: ' : 'Your note: '}</span>{offer.contractor_response_note}
        </p>
      )}

      {/* Phase 86AZ.56 — Status banner so the contractor knows exactly
          where the offer sits in the lifecycle. */}
      {offer.status === 'accepted' && (
        <div data-testid={`direct-offer-accepted-banner-${offer.id}`}
          className="flex items-start gap-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 mb-2">
          <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p>{tr ? 'Teklifi kabul ettiniz.' : 'You accepted the offer.'}</p>
            <p className="font-normal text-emerald-600/90">
              {tr ? 'Yönetici nihai onayı verdiğinde proje "Projeler" sekmesinde görünecek ve size bildirim gelecektir.'
                  : 'Once the admin gives the final approval, the project will appear under "Projects" and you will be notified.'}
            </p>
          </div>
        </div>
      )}
      {offer.status === 'rejected' && (
        <div data-testid={`direct-offer-rejected-banner-${offer.id}`}
          className="flex items-start gap-2 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-2">
          <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p>{offer.rejection_reason === 'awarded_to_other'
              ? (tr ? 'Bu defa başka bir yüklenici seçildi.' : 'Another contractor was selected this time.')
              : (tr ? 'Bu teklifi reddettiniz.' : 'You rejected this offer.')}</p>
            <p className="font-normal text-red-600/90">
              {tr ? 'Teklif kapatıldı; sohbet salt-okunur olarak arşivlendi.'
                  : 'The offer is closed; the chat is archived as read-only.'}
            </p>
          </div>
        </div>
      )}
      {offer.status === 'finalized' && (
        <div data-testid={`direct-offer-finalized-banner-${offer.id}`}
          className="flex items-start gap-2 text-xs font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded-xl px-3 py-2 mb-2">
          <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p>{tr ? 'Proje tarafınıza atandı.' : 'Project assigned to you.'}</p>
            <p className="font-normal text-violet-600/90">
              {tr ? 'Üretim sürecine başlayabilirsiniz. Yöneticiyle iletişimde kalın.'
                  : 'You may start the production process. Stay in touch with the admin.'}
            </p>
          </div>
        </div>
      )}

      {isPending && (
        <div className="space-y-2">
          {showRejectNote && (
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={1000}
              placeholder={tr ? 'Red gerekçesi (opsiyonel)…' : 'Reject reason (optional)…'}
              data-testid="direct-offer-respond-note"
              className="w-full text-sm border border-red-200 rounded-lg p-2 bg-white focus:ring-2 focus:ring-red-200 focus:outline-none" />
          )}
          <div className="flex gap-2">
            <button type="button" disabled={submitting}
              data-testid={`direct-offer-accept-${offer.id}`}
              onClick={() => respond(true)}
              className="flex-1 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
              <CheckCircle className="h-4 w-4" />
              {tr ? 'Kabul Et' : 'Accept'}
            </button>
            <button type="button" disabled={submitting}
              data-testid={`direct-offer-reject-${offer.id}`}
              onClick={() => { if (!showRejectNote) setShowRejectNote(true); else respond(false); }}
              className="flex-1 px-3 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
              <XCircle className="h-4 w-4" />
              {tr ? 'Reddet' : 'Reject'}
            </button>
          </div>
        </div>
      )}
        </div>

        {/* Right column — always-on Chat */}
        <div className="lg:col-span-2 min-w-0">
          <ContractorOfferChat offer={offer} tr={tr} inline />
        </div>
      </div>
    </div>
  );
}

// Phase 86AZ.46 — Direct offer chat for contractor (admin ↔ contractor).
// Phase 86AZ.53 — Supports an `inline` layout (always-open, fills parent
// column on the right side of the project card) and per-message file
// preview lightbox so attachments expand on click instead of forcing a
// raw download/new-tab.
// Phase 86AZ.54 — Module-level blob-URL cache so opening the SAME chat
// attachment a second time is instant (no re-download). Kubernetes ingress
// strips backend Cache-Control, so we cache on the client side by URL.
// LRU semantics: 30 items max (~roughly 300 MB worst case for PDFs).
const __chatBlobCache = (typeof window !== 'undefined' && window.__chatBlobCache)
  || (typeof window !== 'undefined' ? (window.__chatBlobCache = new Map()) : new Map());

async function getCachedBlobUrl(url, token) {
  if (!url) return url;
  const hit = __chatBlobCache.get(url);
  if (hit) {
    // Touch for LRU
    __chatBlobCache.delete(url);
    __chatBlobCache.set(url, hit);
    return hit;
  }
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const resp = await fetch(url, { headers, credentials: 'same-origin' });
    if (!resp.ok) return url; // fall back to direct URL
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    __chatBlobCache.set(url, blobUrl);
    // Cap at 30 entries — evict oldest.
    while (__chatBlobCache.size > 30) {
      const oldest = __chatBlobCache.keys().next().value;
      const oldUrl = __chatBlobCache.get(oldest);
      __chatBlobCache.delete(oldest);
      try { URL.revokeObjectURL(oldUrl); } catch { /* ignore */ }
    }
    return blobUrl;
  } catch {
    return url;
  }
}

function ContractorOfferChat({ offer, tr, inline = false }) {
  const [open, setOpen] = useState(inline);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { url, name, ext, blobUrl?, loading? }
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== 'undefined' ? localStorage.getItem('cop31_contractor_token') : null;

  // When the lightbox opens, hydrate it with a cached blob URL so the same
  // attachment, opened twice, doesn't re-download.
  const openLightbox = async ({ url, name, ext }) => {
    // Synchronously open with raw URL so the modal renders immediately;
    // then upgrade to the blob URL once cached. Subsequent opens get the
    // cached blob URL instantly via the upfront cache hit check.
    const cached = __chatBlobCache.get(url);
    setLightbox({ url, name, ext, blobUrl: cached, loading: !cached });
    if (!cached) {
      const blobUrl = await getCachedBlobUrl(url, token);
      setLightbox(curr => (curr && curr.url === url ? { ...curr, blobUrl, loading: false } : curr));
    }
  };

  const fetchMsgs = async () => {
    if (!open || !token) return;
    try {
      const r = await axios.get(`${API_BASE}/api/stand-designs/direct-offers/${offer.id}/messages`,
        { headers: { Authorization: `Bearer ${token}` } });
      setMessages(r.data?.messages || []);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    if (!open) return;
    fetchMsgs();
    const t = setInterval(fetchMsgs, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [open, offer.id]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const locked = offer.status === 'rejected' || offer.status === 'cancelled' || offer.chat_locked;

  const send = async () => {
    if (locked) return;
    if (!text.trim() && !file) return;
    setSending(true);
    try {
      const fd = new FormData();
      if (text.trim()) fd.append('message', text);
      if (file) fd.append('attachment', file);
      await axios.post(`${API_BASE}/api/stand-designs/direct-offers/${offer.id}/messages`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setText(''); setFile(null);
      await fetchMsgs();
    } catch (e) {
      alert(e?.response?.data?.detail || 'failed');
    } finally { setSending(false); }
  };

  const isImageName = (name) => /\.(jpe?g|png|webp|gif|bmp|heic|heif|svg)$/i.test(name || '');
  const isPdfName   = (name) => /\.pdf$/i.test(name || '');

  // Message bubble + per-attachment thumbnail.
  const renderMessages = () => (
    messages.length === 0 ? (
      <p className="text-xs text-gray-400 text-center py-6">{tr ? 'Henüz mesaj yok.' : 'No messages yet.'}</p>
    ) : messages.map(m => {
      const mine = m.sender_role === 'contractor';
      const att = m.attachment;
      const attUrl = att ? `${API_BASE}/api/uploads/${att.filename}` : null;
      const attIsImg = att && isImageName(att.original_name || att.filename);
      const attIsPdf = att && isPdfName(att.original_name || att.filename);
      return (
        <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm shadow-sm ${
            mine ? 'bg-orange-500 text-white rounded-br-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
          }`}>
            {m.message && <p className="whitespace-pre-wrap break-words">{m.message}</p>}
            {att && attIsImg && (
              <button type="button"
                onClick={() => openLightbox({ url: attUrl, name: att.original_name, ext: 'img' })}
                className="block mt-1.5 rounded-lg overflow-hidden border border-white/30 hover:opacity-90 transition-opacity"
                data-testid={`chat-attachment-img-${m.id}`}>
                <img src={attUrl} alt={att.original_name}
                  className="max-w-[220px] max-h-[160px] object-cover block bg-gray-100" />
              </button>
            )}
            {att && attIsPdf && (
              <button type="button"
                onClick={() => openLightbox({ url: attUrl, name: att.original_name, ext: 'pdf' })}
                className={`mt-1.5 flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-semibold ${
                  mine ? 'bg-white/15 border-white/30 hover:bg-white/25 text-white'
                       : 'bg-red-50 border-red-200 hover:bg-red-100 text-red-700'}`}
                data-testid={`chat-attachment-pdf-${m.id}`}>
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${mine ? 'bg-white/30 text-white' : 'bg-red-200 text-red-800'}`}>PDF</span>
                <span className="max-w-[160px] truncate">{att.original_name}</span>
              </button>
            )}
            {att && !attIsImg && !attIsPdf && (
              <a href={attUrl} target="_blank" rel="noopener noreferrer"
                className={`mt-1 inline-flex items-center gap-1 text-xs underline ${mine ? 'text-white/90' : 'text-orange-600'}`}>
                <Paperclip className="h-3 w-3" />{att.original_name}
              </a>
            )}
            <p className={`text-[9px] mt-1 ${mine ? 'text-white/70' : 'text-gray-400'}`}>
              {new Date(m.created_at).toLocaleTimeString(tr ? 'tr-TR' : 'en-US', {hour: '2-digit', minute: '2-digit'})}
            </p>
          </div>
        </div>
      );
    })
  );

  const inputBar = (
    <div className="border-t border-gray-100 p-2 flex items-center gap-2 bg-white">
      <input ref={fileRef} type="file" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
      <button type="button" onClick={() => fileRef.current?.click()} disabled={locked}
        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed">
        <Paperclip className="h-4 w-4" />
      </button>
      {file && (
        <span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-1 rounded-full max-w-[100px] truncate">
          {file.name}<button onClick={() => setFile(null)} className="ml-1 text-red-500">×</button>
        </span>
      )}
      <input type="text" value={text} onChange={e => setText(e.target.value)} disabled={locked}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
        placeholder={locked ? (tr ? 'Sohbet kapatıldı' : 'Chat is closed') : (tr ? 'Mesaj yazın…' : 'Type a message…')}
        className="flex-1 text-sm border border-gray-200 rounded-full px-3 py-1.5 focus:ring-2 focus:ring-orange-200 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" />
      <button type="button" onClick={send} disabled={sending || locked || (!text.trim() && !file)}
        className="p-1.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-40">
        <Send className="h-3.5 w-3.5" />
      </button>
    </div>
  );

  // Phase 86AZ.54 — Helper for the lightbox download button: backend now
  // accepts ?disposition=attachment to force the Save-As dialog; the preview
  // URL (inline) is cache-friendly and shared with thumbnails so opening
  // the same file twice is instant after the first hit.
  const toDownloadUrl = (u) => (u ? `${u}${u.includes('?') ? '&' : '?'}disposition=attachment` : u);

  // Per-chat lightbox modal for attachments.
  const lightboxNode = lightbox && (
    <div className="fixed inset-0 z-[10010] bg-black/90 flex items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) setLightbox(null); }}
      data-testid="chat-attachment-modal">
      <div className="absolute top-0 left-0 right-0 px-5 py-3 flex items-center justify-between text-white bg-gradient-to-b from-black/60 to-transparent">
        <span className="text-xs text-white/80 truncate max-w-[60%]">{lightbox.name}</span>
        <div className="flex items-center gap-2">
          <a href={toDownloadUrl(lightbox.url)} download={lightbox.name} target="_blank" rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold">
            {tr ? 'İndir' : 'Download'}
          </a>
          <button type="button" onClick={() => setLightbox(null)}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/10 hover:bg-white/20">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="max-w-[92vw] max-h-[88vh] w-full h-full flex items-center justify-center px-10 py-14">
        {lightbox.loading && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-white/70 text-xs font-medium animate-pulse">
              {tr ? 'Yükleniyor…' : 'Loading…'}
            </div>
          </div>
        )}
        {lightbox.ext === 'img' ? (
          <img src={lightbox.blobUrl || lightbox.url} alt={lightbox.name} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
        ) : (
          <iframe src={lightbox.blobUrl || lightbox.url} title={lightbox.name} className="w-full h-full bg-white rounded-lg shadow-2xl" />
        )}
      </div>
    </div>
  );

  // ── INLINE MODE: full-height column embedded in parent grid ──
  if (inline) {
    return (
      <div className="bg-white rounded-2xl border border-orange-100 shadow-sm flex flex-col overflow-hidden h-full min-h-[420px]"
        data-testid={`contractor-offer-chat-${offer.id}`}>
        <div className="px-3 py-2.5 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100 flex items-center gap-2 text-sm font-bold text-orange-700">
          <Send className="h-3.5 w-3.5" />
          {tr ? 'Yönetici ile Sohbet' : 'Chat with Admin'}
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
          {locked && (
            <div data-testid={`contractor-chat-locked-${offer.id}`}
              className="text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-1 text-center">
              {tr ? 'Bu teklif kapatıldı — sohbet salt-okunur. Yeni mesaj gönderilemez.'
                  : 'This offer is closed — chat is read-only. New messages cannot be sent.'}
            </div>
          )}
          {renderMessages()}
        </div>
        {inputBar}
        {lightboxNode}
      </div>
    );
  }

  // ── DEFAULT (collapsible) — kept for backwards-compat ──
  return (
    <div className="border border-gray-200 rounded-xl my-2 overflow-hidden" data-testid={`contractor-offer-chat-${offer.id}`}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full px-3 py-2 bg-orange-50 hover:bg-orange-100 flex items-center justify-between text-sm font-semibold text-orange-700 transition-colors">
        <span className="flex items-center gap-2">
          <Send className="h-3.5 w-3.5" />
          {tr ? 'Yönetici ile Sohbet' : 'Chat with Admin'}
        </span>
        <span className="text-xs">{open ? '▼' : '▶'}</span>
      </button>
      {open && (
        <div className="bg-white">
          <div ref={scrollRef} className="overflow-y-auto p-3 space-y-2 bg-gray-50" style={{ maxHeight: 300, minHeight: 160 }}>
            {locked && (
              <div data-testid={`contractor-chat-locked-${offer.id}`}
                className="text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-1 text-center">
                {tr ? 'Bu teklif kapatıldı — sohbet salt-okunur. Yeni mesaj gönderilemez.'
                    : 'This offer is closed — chat is read-only. New messages cannot be sent.'}
              </div>
            )}
            {renderMessages()}
          </div>
          {inputBar}
          {lightboxNode}
        </div>
      )}
    </div>
  );
}

export default function ContractorDashboard() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { language } = useLanguage();
  const tr = language === 'tr';

  const [activeSection, setActiveSection] = useState('dashboard');
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const [bids, setBids] = useState([]);
  const [selectedRfq, setSelectedRfq] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [msgFile, setMsgFile] = useState(null);
  const [msgLoading, setMsgLoading] = useState(false);
  const [bidForm, setBidForm] = useState({ amount: '', currency: 'usd', validity_value: '', validity_unit: 'gün', notes: '', accept_payment_terms: false, accept_work_conditions: false, accept_safety_rules: false });
  const [bidLoading, setBidLoading] = useState(false);
  const [bidSuccessModal, setBidSuccessModal] = useState({ open: false, bid: null });
  const [loading, setLoading] = useState(true);
  // Phase 86AZ.40 — Direct price offers from admin (no-bidding flow)
  const [directOffers, setDirectOffers] = useState([]);
  const fetchDirectOffers = async () => {
    try {
      const token = localStorage.getItem('cop31_contractor_token');
      if (!token) return;
      const r = await axios.get(`${API}/stand-designs/contractor/direct-offers`,
        { headers: { Authorization: `Bearer ${token}` } });
      setDirectOffers(r.data?.offers || []);
    } catch {}
  };
  useEffect(() => {
    fetchDirectOffers();
    const t = setInterval(fetchDirectOffers, 15000);
    return () => clearInterval(t);
  }, []);
  // Phase 86AZ.26 — Classic top-right user menu (replaces bottom-sidebar
  // logout). Toggled by the avatar button; closes on outside click / Esc.
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDocClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setUserMenuOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);
  const [unreadAdminMsgs, setUnreadAdminMsgs] = useState(0);
  const msgFileRef = useRef(null);
  const messagesEndRef = useRef(null);
  const pollMsgRef = useRef(null);
  const selectedRfqRef = useRef(null);
  const pollUnreadRef = useRef(null);

  const token = localStorage.getItem('cop31_contractor_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/giris?tab=contractor'); return; }
    fetchDashboard();
    // Poll unread admin messages every 6s
    const fetchUnread = async () => {
      try {
        const r = await axios.get(`${API}/contractor/unread-count`, { headers });
        setUnreadAdminMsgs(r.data.unread || 0);
      } catch {}
    };
    fetchUnread();
    pollUnreadRef.current = setInterval(fetchUnread, 6000);
    return () => clearInterval(pollUnreadRef.current);
  }, []);

  useEffect(() => {
    if (selectedRfq) {
      fetchMessages(selectedRfq.id);
      markRfqMessagesRead(selectedRfq.id);
      // Poll messages every 5s while rfq detail is open
      selectedRfqRef.current = selectedRfq;
      clearInterval(pollMsgRef.current);
      pollMsgRef.current = setInterval(() => {
        if (selectedRfqRef.current) fetchMessages(selectedRfqRef.current.id);
      }, 5000);
      // Pre-fill bid form if bid exists
      const latestBid = selectedRfq.bids?.[0];
      if (latestBid) {
        setBidForm({
          amount: latestBid.amount || '',
          currency: 'usd', // Phase 86AL — platform-wide USD enforcement
          validity_value: latestBid.validity_value || '',
          validity_unit: latestBid.validity_unit || 'gün',
          notes: latestBid.notes || '',
          accept_payment_terms: latestBid.accept_payment_terms || false,
          accept_work_conditions: latestBid.accept_work_conditions || false,
          accept_safety_rules: latestBid.accept_safety_rules || false,
        });
      } else {
        setBidForm({ amount: '', currency: 'usd', validity_value: '', validity_unit: 'gün', notes: '', accept_payment_terms: false, accept_work_conditions: false, accept_safety_rules: false });
      }
    } else {
      clearInterval(pollMsgRef.current);
      selectedRfqRef.current = null;
    }
    return () => clearInterval(pollMsgRef.current);
  }, [selectedRfq?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchDashboard = async () => {
    try {
      const [profileRes, statsRes, rfqsRes, bidsRes] = await Promise.allSettled([
        axios.get(`${API}/contractor/profile`, { headers }),
        axios.get(`${API}/contractor/stats`, { headers }),
        axios.get(`${API}/contractor/rfqs`, { headers }),
        axios.get(`${API}/contractor/bids`, { headers }),
      ]);
      if (profileRes.status === 'fulfilled') setProfile(profileRes.value.data);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (rfqsRes.status === 'fulfilled') setRfqs(rfqsRes.value.data || []);
      if (bidsRes.status === 'fulfilled') setBids(bidsRes.value.data || []);
    } catch {}
    finally { setLoading(false); }
  };

  const fetchMessages = async (rfqId) => {
    try {
      const r = await axios.get(`${API}/contractor/rfqs/${rfqId}/messages`, { headers });
      setMessages(r.data || []);
    } catch {}
  };

  const markRfqMessagesRead = async (rfqId) => {
    try {
      await axios.post(`${API}/contractor/rfqs/${rfqId}/messages/mark-read`, {}, { headers });
      setUnreadAdminMsgs(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleLogout = () => {
    localStorage.removeItem('cop31_contractor_token');
    navigate('/giris?tab=contractor');
  };

  const openRfq = async (rfq) => {
    // Fetch full RFQ detail
    try {
      const r = await axios.get(`${API}/contractor/rfqs/${rfq.id}`, { headers });
      setSelectedRfq(r.data);
    } catch {
      setSelectedRfq(rfq);
    }
    setActiveSection('rfq-detail');
  };

  const sendMessage = async () => {
    if (!msgText.trim() && !msgFile) return;
    setMsgLoading(true);
    try {
      const fd = new FormData();
      fd.append('message', msgText);
      if (msgFile) fd.append('attachment', msgFile);
      const r = await axios.post(`${API}/contractor/rfqs/${selectedRfq.id}/messages`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setMessages(prev => [...prev, r.data]);
      setMsgText('');
      setMsgFile(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mesaj gönderilemedi.');
    } finally { setMsgLoading(false); }
  };

  const submitBid = async () => {
    if (!bidForm.amount) { toast.error(tr ? 'Teklif tutarı zorunludur.' : 'Bid amount is required.'); return; }
    if (!bidForm.accept_payment_terms || !bidForm.accept_work_conditions || !bidForm.accept_safety_rules) {
      toast.error(tr ? 'Devam etmek için tüm koşulları kabul etmelisiniz.' : 'You must accept all terms to continue.');
      return;
    }
    setBidLoading(true);
    try {
      const fd = new FormData();
      // Strip formatting dots before sending
      const rawAmount = String(bidForm.amount).replace(/\./g, '');
      fd.append('amount', rawAmount);
      fd.append('currency', bidForm.currency);
      if (bidForm.validity_value) fd.append('validity_value', bidForm.validity_value);
      if (bidForm.validity_unit) fd.append('validity_unit', bidForm.validity_unit);
      if (bidForm.notes) fd.append('notes', bidForm.notes);
      fd.append('accept_payment_terms', bidForm.accept_payment_terms);
      fd.append('accept_work_conditions', bidForm.accept_work_conditions);
      fd.append('accept_safety_rules', bidForm.accept_safety_rules);
      const r = await axios.post(`${API}/contractor/rfqs/${selectedRfq.id}/bid`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      // Show success modal
      setBidSuccessModal({ open: true, bid: r.data });
      // Add new bid to local state immediately
      setSelectedRfq(prev => ({
        ...prev,
        bids: [r.data, ...(prev.bids || [])],
        status: 'bid_submitted'
      }));
      fetchDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Teklif gönderilemedi.');
    } finally { setBidLoading(false); }
  };

  const companyName = profile?.tenant?.company_name || profile?.user?.full_name || 'Yüklenici';

  // Phase 86AZ.55 — Badges reflect the new routing:
  //   • Requests = pending/accepted/rejected direct offers (awaiting work)
  //   • Projects = finalized direct offers (awarded work)
  const pendingDirectOffersCount = (directOffers || []).filter(o => o.status === 'pending').length;
  const inflightCount = (directOffers || []).filter(o => o.status !== 'finalized').length;
  const awardedCount = (directOffers || []).filter(o => o.status === 'finalized').length;

  const navItems = [
    { id: 'dashboard', icon: Hammer, label: tr ? 'Özet' : 'Overview' },
    { id: 'rfqs', icon: ClipboardList, label: tr ? 'Talepler' : 'Requests',
      badge: (pendingDirectOffersCount + unreadAdminMsgs) || null },
    // Phase 86AZ.48 — "Direct Offers" rebranded as "Projects" for clarity.
    { id: 'direct-offers', icon: DollarSign, label: tr ? 'Projeler' : 'Projects',
      badge: awardedCount || null },
    // Phase 86AZ.57 — ISG (Occupational Health & Safety) personnel management.
    { id: 'isg', icon: ShieldCheck, label: tr ? 'ISG' : 'ISG' },
    // Phase 86AZ.48 — "My Bids" hidden for now (no bidding flow active).
    // { id: 'bids', icon: FileText, label: tr ? 'Tekliflerim' : 'My Bids' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full" />
          <p className="text-gray-500 text-sm">{tr ? 'Yükleniyor...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-gradient-to-b from-orange-900 to-red-900 text-white flex flex-col shrink-0 hidden md:flex">
        <div className="px-6 py-5 border-b border-orange-800/50">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="h-9 w-9 bg-orange-500/30 rounded-xl flex items-center justify-center">
              <Hammer className="h-5 w-5 text-orange-200" />
            </div>
            <span className="font-bold text-white text-sm leading-tight">COP 31<br/><span className="text-orange-300 text-xs font-normal">{tr ? 'Yüklenici' : 'Contractor'}</span></span>
          </div>
          <p className="text-orange-200 text-xs truncate mt-2">{companyName}</p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map(({ id, icon: Icon, label, badge }) => (
            <button key={id} data-testid={`contractor-nav-${id}`}
              onClick={() => setActiveSection(id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm font-medium ${
                activeSection === id || (activeSection === 'rfq-detail' && id === 'rfqs')
                  ? 'bg-orange-500/30 text-white' : 'text-orange-200/80 hover:bg-orange-800/30 hover:text-white'
              }`}>
              <Icon className="h-4 w-4 flex-shrink-0" />
              {label}
              {id === 'rfqs' && badge > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full animate-pulse">{badge}</span>
              )}
            </button>
          ))}
        </nav>
        {/* Phase 86AZ.26 — Logout & Language moved to the top-right user
            menu in the header; sidebar bottom block intentionally removed. */}
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-bold text-gray-900 text-lg">
              {activeSection === 'dashboard' && (tr ? 'Yüklenici Paneli' : 'Contractor Dashboard')}
              {activeSection === 'rfqs' && (tr ? 'Gelen Talepler' : 'Incoming Requests')}
              {activeSection === 'rfq-detail' && (tr ? 'Talep Detayı' : 'Request Detail')}
              {activeSection === 'bids' && (tr ? 'Tekliflerim' : 'My Bids')}
              {activeSection === 'direct-offers' && (tr ? 'Projeler' : 'Projects')}
              {activeSection === 'isg' && (tr ? 'ISG — İş Sağlığı & Güvenliği' : 'ISG — Health & Safety')}
            </h1>
            <p className="text-gray-400 text-xs">{companyName}</p>
          </div>

          {/* Phase 86AZ.26 — Classic top-right user menu (avatar + dropdown).
              Replaces the sidebar-bottom Logout + mobile X icon. */}
          <div className="relative" ref={userMenuRef}>
            <button
              data-testid="contractor-user-menu-trigger"
              type="button"
              onClick={() => setUserMenuOpen(o => !o)}
              className="flex items-center gap-2.5 px-2 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
            >
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                {(companyName || '?').charAt(0).toUpperCase()}
              </div>
              <div className="hidden sm:flex flex-col items-start max-w-[160px]">
                <span className="text-xs font-semibold text-gray-800 truncate w-full">{companyName}</span>
                <span className="text-[10px] text-gray-400 truncate w-full">
                  {profile?.user?.email || (tr ? 'Yüklenici' : 'Contractor')}
                </span>
              </div>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {userMenuOpen && (
              <div
                data-testid="contractor-user-menu"
                className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50"
              >
                {/* Header summary */}
                <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-br from-orange-50 to-red-50">
                  <p className="text-sm font-bold text-gray-900 truncate">{companyName}</p>
                  {profile?.user?.email && (
                    <p className="text-xs text-gray-500 truncate mt-0.5">{profile.user.email}</p>
                  )}
                  <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-bold uppercase tracking-wide bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                    <Hammer className="h-2.5 w-2.5" />
                    {tr ? 'Yüklenici Hesabı' : 'Contractor Account'}
                  </span>
                </div>

                {/* Items */}
                <div className="py-1.5">
                  <button
                    type="button"
                    data-testid="contractor-user-menu-settings"
                    onClick={() => { setUserMenuOpen(false); toast.info(tr ? 'Ayarlar yakında eklenecek.' : 'Settings coming soon.'); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <SettingsIcon className="h-4 w-4 text-gray-400" />
                    <span>{tr ? 'Ayarlar' : 'Settings'}</span>
                  </button>
                  <button
                    type="button"
                    data-testid="contractor-user-menu-profile"
                    onClick={() => { setUserMenuOpen(false); setActiveSection('dashboard'); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <User className="h-4 w-4 text-gray-400" />
                    <span>{tr ? 'Profilim' : 'My Profile'}</span>
                  </button>
                  {/* Language row — inline toggle, doesn't close the menu */}
                  <div className="px-4 py-2.5 flex items-center justify-between border-t border-gray-100 mt-1 pt-3">
                    <div className="flex items-center gap-3 text-sm text-gray-700">
                      <Globe className="h-4 w-4 text-gray-400" />
                      <span>{tr ? 'Dil' : 'Language'}</span>
                    </div>
                    <LanguageToggle variant="admin" />
                  </div>
                </div>

                {/* Divider + Logout */}
                <div className="border-t border-gray-100">
                  <button
                    type="button"
                    data-testid="contractor-logout"
                    onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>{tr ? 'Çıkış Yap' : 'Logout'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        <div className="p-6">

          {/* ─── DASHBOARD ─── */}
          {activeSection === 'dashboard' && (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: tr ? 'Toplam Talep' : 'Total Requests', value: stats?.total_rfqs ?? '-', color: 'bg-blue-50 text-blue-600', icon: ClipboardList },
                  { label: tr ? 'Yeni Talep' : 'New Requests', value: stats?.new_rfqs ?? '-', color: 'bg-orange-50 text-orange-600', icon: Clock },
                  { label: tr ? 'Verilen Teklif' : 'Bids Submitted', value: stats?.total_bids ?? '-', color: 'bg-purple-50 text-purple-600', icon: FileText },
                  { label: tr ? 'Kazanılan' : 'Won', value: stats?.won_bids ?? '-', color: 'bg-emerald-50 text-emerald-600', icon: CheckCircle },
                ].map(({ label, value, color, icon: Icon }) => (
                  <div key={label} className="bg-white rounded-2xl border border-gray-100 p-5">
                    <div className={`h-9 w-9 ${color} rounded-xl flex items-center justify-center mb-3`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{value}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>

              {/* Recent RFQs */}
              <div className="bg-white rounded-2xl border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="font-semibold text-gray-800">{tr ? 'Son Talepler' : 'Recent Requests'}</h2>
                  <button onClick={() => setActiveSection('rfqs')} className="text-orange-600 text-sm font-medium hover:underline">
                    {tr ? 'Tümünü Gör' : 'View All'}
                  </button>
                </div>
                {rfqs.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    <ClipboardList className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{tr ? 'Henüz talep yok.' : 'No requests yet.'}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {rfqs.slice(0, 5).map(rfq => (
                      <button key={rfq.id} onClick={() => openRfq(rfq)}
                        className="w-full flex items-center justify-between px-6 py-4 hover:bg-orange-50/50 transition-colors text-left">
                        <div>
                          <p className="font-medium text-gray-800 text-sm">{rfq.design?.title || 'Stand Tasarım Talebi'}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{rfq.design?.hall && `Hall: ${rfq.design.hall}`} {rfq.design?.stand_no && `| Stand No: ${rfq.design.stand_no}`}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={rfq.status} />
                          <ChevronRight className="h-4 w-4 text-gray-300" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── RFQ LIST ─── */}
          {/* ─── REQUESTS — Pending/accepted/rejected direct offers + legacy RFQs ───
              Phase 86AZ.55 — A direct price offer (Get Price flow) lands here
              first. Once the admin finalizes/awards it, the card moves to the
              "Projects" tab automatically. */}
          {activeSection === 'rfqs' && (() => {
            const inflightOffers = directOffers.filter(o => o.status !== 'finalized');
            const hasNothing = rfqs.length === 0 && inflightOffers.length === 0;
            return (
              <div className="space-y-6">
                {hasNothing && (
                  <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                    <ClipboardList className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{tr ? 'Henüz size gelen bir talep yok.' : 'No requests yet.'}</p>
                  </div>
                )}

                {/* Direct-offer requests (the active flow) */}
                {inflightOffers.length > 0 && (
                  <div className="space-y-4">
                    {inflightOffers.map(o => (
                      <DirectOfferCard key={o.id} offer={o} tr={tr} onResponded={fetchDirectOffers} />
                    ))}
                  </div>
                )}

                {/* Legacy RFQ list (kept hidden when empty) */}
                {rfqs.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {rfqs.map(rfq => {
                    const latestBid = rfq.bids?.[0];
                    const bidCount = rfq.bids?.length || 0;
                    const statusColors = {
                      pending: { bg: 'bg-blue-50', border: 'border-blue-100', dot: 'bg-blue-400', text: 'text-blue-700', label: tr ? 'Yeni' : 'New' },
                      viewed: { bg: 'bg-purple-50', border: 'border-purple-100', dot: 'bg-purple-400', text: 'text-purple-700', label: tr ? 'İncelendi' : 'Viewed' },
                      bid_submitted: { bg: 'bg-amber-50', border: 'border-amber-100', dot: 'bg-amber-400', text: 'text-amber-700', label: tr ? 'Teklif Verildi' : 'Bid Submitted' },
                      awarded: { bg: 'bg-emerald-50', border: 'border-emerald-100', dot: 'bg-emerald-400', text: 'text-emerald-700', label: tr ? 'Kazanıldı' : 'Won' },
                      not_awarded: { bg: 'bg-gray-50', border: 'border-gray-100', dot: 'bg-gray-400', text: 'text-gray-500', label: tr ? 'Kazanılamadı' : 'Not Won' },
                    };
                    const sc = statusColors[rfq.status] || statusColors.pending;
                    return (
                      <div key={rfq.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-orange-200 hover:shadow-md transition-all group flex flex-col">
                        {/* Card Header */}
                        <div className="px-5 pt-5 pb-4">
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="h-10 w-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                              <Building2 className="h-5 w-5 text-orange-500" />
                            </div>
                            <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${sc.bg} ${sc.text}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${sc.dot}`} />
                              {sc.label}
                            </span>
                          </div>
                          <h3 className="font-bold text-gray-800 text-sm leading-snug line-clamp-2 mb-1">
                            {rfq.design?.title || 'Stand Tasarım Talebi'}
                          </h3>
                          <div className="flex items-center gap-2 flex-wrap mt-1">
                            {rfq.design?.hall && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                                Hall {rfq.design.hall}
                              </span>
                            )}
                            {rfq.design?.stand_no && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                                Stand {rfq.design.stand_no}
                              </span>
                            )}
                            {rfq.design?.stand_area && (
                              <span className="text-xs bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full font-medium">
                                {rfq.design.stand_area} m²
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Bid Summary */}
                        {latestBid ? (
                          <div className="mx-4 mb-4 rounded-xl bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-100 px-4 py-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-400">{tr ? 'Son Teklif' : 'Latest Bid'}</span>
                              {bidCount > 1 && (
                                <span className="text-xs bg-orange-100 text-orange-600 font-semibold px-2 py-0.5 rounded-full">
                                  {bidCount} {tr ? 'teklif' : 'bids'}
                                </span>
                              )}
                            </div>
                            <div className="flex items-end justify-between">
                              <span className="text-xl font-bold text-orange-600">
                                {Number(latestBid.amount).toLocaleString('tr-TR')}
                              </span>
                              <span className="text-sm font-semibold text-orange-500">
                                {(latestBid.currency || 'USD').toUpperCase()}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-1.5 text-xs text-gray-500 pt-1 border-t border-orange-100">
                              {latestBid.validity_value && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3 text-orange-400" />
                                  {latestBid.validity_value} {latestBid.validity_unit || 'gün'}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3 text-gray-400" />
                                {new Date(latestBid.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="mx-4 mb-4 rounded-xl bg-gray-50 border border-dashed border-gray-200 px-4 py-3 text-center">
                            <p className="text-xs text-gray-400">{tr ? 'Henüz teklif verilmedi' : 'No bid submitted yet'}</p>
                          </div>
                        )}

                        {/* Footer */}
                        <div className="mt-auto px-4 pb-4">
                          <button data-testid={`rfq-open-${rfq.id}`} onClick={() => openRfq(rfq)}
                            className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-xl transition-colors group-hover:shadow-sm">
                            {latestBid ? (tr ? 'Aç & Yeni Teklif' : 'Open & New Bid') : (tr ? 'Aç & Teklif Ver' : 'Open & Bid')}
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ─── RFQ DETAIL ─── */}
          {activeSection === 'rfq-detail' && selectedRfq && (
            <div className="space-y-4">
              <button onClick={() => setActiveSection('rfqs')} className="flex items-center gap-2 text-sm text-orange-600 hover:underline font-medium">
                ← {tr ? 'Taleplere Dön' : 'Back to Requests'}
              </button>

              {/* Header */}
              <div className="bg-white rounded-2xl border border-gray-100 px-6 py-4">
                <div className="flex items-start justify-between gap-3">
                  <h2 className="font-bold text-gray-800 text-base flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-orange-500 flex-shrink-0" />
                    {selectedRfq.design?.title || 'Stand Tasarım Talebi'}
                  </h2>
                  <StatusBadge status={selectedRfq.status} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                  {[
                    { label: 'Hall', value: selectedRfq.design?.hall || '-' },
                    { label: 'Stand No', value: selectedRfq.design?.stand_no || '-' },
                    { label: tr ? 'Alan' : 'Area', value: selectedRfq.design?.stand_area ? `${selectedRfq.design.stand_area} m²` : '-' },
                    { label: tr ? 'Son Tarih' : 'Deadline', value: selectedRfq.design?.deadline || '-' },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-400">{label}</p>
                      <p className="font-semibold text-gray-800 text-sm mt-0.5">{value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Awarded banner */}
              {selectedRfq.status === 'awarded' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-4">
                  <CheckCircle className="h-8 w-8 text-emerald-600 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-emerald-800">{tr ? 'Tebrikler! Bu projeyi kazandınız.' : 'Congratulations! You won this project.'}</p>
                    {selectedRfq.bid && <p className="text-sm text-emerald-600 mt-0.5">{tr ? 'Onaylanan teklif:' : 'Approved bid:'} <strong>{selectedRfq.bid.amount?.toLocaleString()} {(selectedRfq.bid.currency || 'USD').toUpperCase()}</strong></p>}
                  </div>
                </div>
              )}

              {/* ── 2-Column Layout ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">

                {/* LEFT: Files + Description + Bid Form */}
                <div className="space-y-4">

                  {/* Description */}
                  {selectedRfq.design?.description && (
                    <div className="bg-white rounded-2xl border border-gray-100 px-5 py-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{tr ? 'Tasarım Notu' : 'Design Notes'}</p>
                      <div className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: selectedRfq.design.description }} />
                    </div>
                  )}

                  {/* Design Files — Compact Grid */}
                  {selectedRfq.design?.design_files?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-gray-100 px-5 py-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                        {tr ? 'Tasarım Dosyaları' : 'Design Files'} ({selectedRfq.design.design_files.length})
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {selectedRfq.design.design_files.map((f, idx) => (
                          <a key={idx}
                            href={`${window.location.origin}/api/uploads/${f.filename}`}
                            target="_blank" rel="noreferrer"
                            className="flex flex-col items-center gap-2 p-3 bg-orange-50 border border-orange-100 rounded-xl hover:bg-orange-100 hover:border-orange-300 transition-all group cursor-pointer">
                            <div className="h-10 w-10 bg-orange-500/10 rounded-xl flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
                              <Paperclip className="h-5 w-5 text-orange-500" />
                            </div>
                            <span className="text-xs text-gray-700 text-center leading-tight line-clamp-2 group-hover:text-orange-700 w-full">{f.original_name}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Submitted Bids List */}
                  {(selectedRfq.bids || []).length > 0 && (
                    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                      <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/60">
                        <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                          <FileText className="h-4 w-4 text-orange-500" />
                          {tr ? `Gönderilen Teklifler (${selectedRfq.bids.length})` : `Submitted Bids (${selectedRfq.bids.length})`}
                        </h3>
                      </div>
                      <div className="divide-y divide-gray-50">
                        {(selectedRfq.bids || []).map((bid, idx) => (
                          <div key={bid.id} className="px-5 py-3.5 flex items-center gap-3 hover:bg-gray-50/60 transition-colors">
                            <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-orange-600 text-xs font-bold">#{(selectedRfq.bids.length - idx)}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-gray-800 text-sm">
                                  {Number(bid.amount).toLocaleString('tr-TR')} {(bid.currency || 'USD').toUpperCase()}
                                </span>
                                {bid.validity_value && (
                                  <span className="text-xs text-gray-400 flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {bid.validity_value} {bid.validity_unit || 'gün'} {tr ? 'geçerli' : 'valid'}
                                  </span>
                                )}
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                  bid.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                                  bid.status === 'rejected' ? 'bg-red-100 text-red-600' :
                                  'bg-blue-50 text-blue-600'
                                }`}>
                                  {bid.status === 'approved' ? (tr ? 'Onaylandı' : 'Approved') :
                                   bid.status === 'rejected' ? (tr ? 'Reddedildi' : 'Rejected') :
                                   (tr ? 'Değerlendirmede' : 'Pending')}
                                </span>
                              </div>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {new Date(bid.created_at).toLocaleString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Bid Form */}
                  {selectedRfq.status !== 'awarded' && selectedRfq.status !== 'not_awarded' && (
                    <div className="bg-white rounded-2xl border border-gray-100 p-5">
                      <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <DollarSign className="h-5 w-5 text-orange-500" />
                        {(selectedRfq.bids || []).length > 0 ? (tr ? 'Yeni Teklif Ver' : 'Submit New Bid') : (tr ? 'Teklif Ver' : 'Submit Bid')}
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        {/* Bid Amount */}
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-gray-600">{tr ? 'Teklif Tutarı' : 'Bid Amount'} <span className="text-red-500">*</span></label>
                          <input data-testid="bid-amount-input"
                            type="text" inputMode="numeric"
                            value={bidForm.amount !== '' ? Number(String(bidForm.amount).replace(/\./g, '') || 0).toLocaleString('tr-TR') : ''}
                            onChange={e => {
                              const stripped = e.target.value.replace(/\./g, '').replace(/[^\d]/g, '');
                              setBidForm(f => ({ ...f, amount: stripped }));
                            }}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                            placeholder="150.000" />
                        </div>
                        {/* Currency — Phase 86AL: locked to USD platform-wide */}
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-gray-600">{tr ? 'Para Birimi' : 'Currency'}</label>
                          <div className="relative">
                            <div
                              data-testid="bid-currency-locked"
                              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-700 font-semibold flex items-center justify-between"
                              title={tr ? 'Tüm teklifler USD cinsinden alınır' : 'All bids are accepted in USD'}
                            >
                              <span>USD – US Dollar</span>
                              <span className="text-[10px] uppercase tracking-wider text-gray-400 bg-white border border-gray-200 px-1.5 py-0.5 rounded">{tr ? 'sabit' : 'fixed'}</span>
                            </div>
                          </div>
                        </div>
                        {/* Validity Period */}
                        <div className="col-span-2 space-y-1.5">
                          <label className="text-xs font-medium text-gray-600 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {tr ? 'Teklif Geçerlilik Süresi' : 'Bid Validity Period'}
                          </label>
                          <div className="flex gap-2">
                            <input type="number" min="1" value={bidForm.validity_value}
                              onChange={e => setBidForm(f => ({ ...f, validity_value: e.target.value }))}
                              className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                              placeholder="30" />
                            <div className="relative w-28">
                              <select value={bidForm.validity_unit}
                                onChange={e => setBidForm(f => ({ ...f, validity_unit: e.target.value }))}
                                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 appearance-none">
                                <option value="gün">{tr ? 'Gün' : 'Day(s)'}</option>
                                <option value="ay">{tr ? 'Ay' : 'Month(s)'}</option>
                                <option value="yıl">{tr ? 'Yıl' : 'Year(s)'}</option>
                              </select>
                              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                            </div>
                          </div>
                        </div>
                        {/* Notes - Rich Text */}
                        <div className="col-span-2 space-y-1.5">
                          <label className="text-xs font-medium text-gray-600">{tr ? 'Notlar / Açıklamalar' : 'Notes / Comments'}</label>
                          <RichTextEditor
                            value={bidForm.notes}
                            onChange={val => setBidForm(f => ({ ...f, notes: val }))}
                            placeholder={tr ? 'Teklif hakkında ek bilgi, koşullar, özel notlar...' : 'Additional info, conditions, special notes...'}
                            minHeight={120}
                          />
                        </div>
                      </div>

                      {/* Acceptance Checkboxes */}
                      <div className="mt-4 space-y-2.5 border-t border-gray-100 pt-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                          <ShieldCheck className="h-3.5 w-3.5 text-orange-500" />
                          {tr ? 'Koşullar ve Onaylar' : 'Terms & Approvals'}
                        </p>
                        {[
                          { key: 'accept_payment_terms', label: tr ? 'Ödeme koşullarını kabul ediyorum' : 'I accept the payment terms', testid: 'bid-accept-payment' },
                          { key: 'accept_work_conditions', label: tr ? 'Çalışma koşullarını kabul ediyorum' : 'I accept the working conditions', testid: 'bid-accept-work' },
                          { key: 'accept_safety_rules', label: tr ? 'Saha güvenlik kurallarını kabul ediyorum' : 'I accept the site safety rules', testid: 'bid-accept-safety' },
                        ].map(({ key, label, testid }) => (
                          <label key={key} className="flex items-start gap-3 cursor-pointer group">
                            <div className="relative flex-shrink-0 mt-0.5">
                              <input type="checkbox" data-testid={testid} checked={bidForm[key]}
                                onChange={e => setBidForm(f => ({ ...f, [key]: e.target.checked }))}
                                className="sr-only peer" />
                              <div className={`h-5 w-5 rounded border-2 transition-all flex items-center justify-center
                                ${bidForm[key] ? 'bg-orange-500 border-orange-500' : 'border-gray-300 bg-white group-hover:border-orange-400'}`}>
                                {bidForm[key] && (
                                  <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                            </div>
                            <span className={`text-sm leading-tight transition-colors ${bidForm[key] ? 'text-gray-800 font-medium' : 'text-gray-500 group-hover:text-gray-700'}`}>
                              {label}
                            </span>
                          </label>
                        ))}
                      </div>

                      <Button data-testid="bid-submit-btn" onClick={submitBid} disabled={bidLoading}
                        className="mt-4 w-full bg-orange-500 hover:bg-orange-600 text-white rounded-xl">
                        {bidLoading
                          ? <span className="flex items-center justify-center gap-2"><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{tr ? 'Gönderiliyor...' : 'Submitting...'}</span>
                          : (tr ? 'Teklif Gönder' : 'Submit Bid')}
                      </Button>
                    </div>
                  )}
                </div>

                {/* RIGHT: Chat */}
                <div className="bg-white rounded-2xl border border-gray-100 flex flex-col" style={{minHeight: 500}}>
                  {/* Chat header */}
                  <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-orange-50 to-white rounded-t-2xl">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">A</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{tr ? 'Admin ile Yazışma' : 'Chat with Admin'}</p>
                        <p className="text-xs text-gray-400">{tr ? 'COP 31 Ekibi' : 'COP 31 Team'}</p>
                      </div>
                    </div>
                    {messages.some(m => m.sender_role === 'ultra_admin' && m.is_read_by_contractor === false) && (
                      <span className="text-xs text-orange-600 font-semibold animate-pulse flex items-center gap-1">
                        <span className="h-2 w-2 bg-orange-500 rounded-full animate-ping" />
                        {tr ? 'Yeni mesaj' : 'New msg'}
                      </span>
                    )}
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1 bg-gray-50/30" style={{maxHeight: 360}}>
                    {messages.length === 0 && (
                      <div className="flex justify-center py-12">
                        <span className="text-xs text-gray-400 bg-white border border-gray-100 rounded-full px-4 py-2">
                          {tr ? 'Henüz mesaj yok — ilk mesajı gönderin.' : 'No messages yet — start the conversation.'}
                        </span>
                      </div>
                    )}
                    {messages.map((msg, idx) => {
                      const isMe = msg.sender_role === 'contractor_admin';
                      const prevMsg = messages[idx - 1];
                      const showDate = !prevMsg || new Date(msg.created_at).toDateString() !== new Date(prevMsg.created_at).toDateString();
                      return (
                        <React.Fragment key={msg.id}>
                          {showDate && (
                            <div className="flex items-center gap-3 py-2">
                              <div className="flex-1 h-px bg-gray-200" />
                              <span className="text-xs text-gray-400 font-medium whitespace-nowrap">
                                {new Date(msg.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })}
                              </span>
                              <div className="flex-1 h-px bg-gray-200" />
                            </div>
                          )}
                          <div className={`flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                            {!isMe && (
                              <div className="h-7 w-7 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center flex-shrink-0 mb-0.5">
                                <span className="text-white text-xs font-bold">A</span>
                              </div>
                            )}
                            <div className="max-w-[78%]">
                              {!isMe && <p className="text-xs font-semibold text-gray-500 mb-1 ml-1">{msg.sender_name}</p>}
                              <div className={`px-3.5 py-2.5 rounded-2xl shadow-sm text-sm leading-relaxed
                                ${isMe ? 'bg-orange-500 text-white rounded-br-none' : 'bg-white border border-gray-100 text-gray-800 rounded-bl-none'}`}>
                                <p>{msg.message}</p>
                                {msg.attachment && (
                                  <a href={`${window.location.origin}/api/uploads/${msg.attachment.filename}`} target="_blank" rel="noreferrer"
                                    className={`flex items-center gap-1.5 mt-2 text-xs rounded-lg px-2 py-1 ${isMe ? 'bg-orange-400/50 text-orange-100' : 'bg-orange-50 text-orange-600'} transition-colors`}>
                                    <Paperclip className="h-3 w-3 flex-shrink-0" /><span className="truncate">{msg.attachment.original_name}</span>
                                  </a>
                                )}
                              </div>
                              <div className={`flex items-center gap-1 mt-1 px-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                                <span className="text-xs text-gray-400">
                                  {new Date(msg.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  <div className="px-4 py-3 border-t border-gray-100 bg-white rounded-b-2xl">
                    {msgFile && (
                      <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-xl">
                        <Paperclip className="h-3 w-3 text-orange-500" />
                        <span className="text-xs flex-1 truncate text-gray-700">{msgFile.name}</span>
                        <button onClick={() => setMsgFile(null)}><X className="h-3 w-3 text-gray-400 hover:text-red-500" /></button>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <input ref={msgFileRef} type="file" className="hidden"
                        onChange={e => { setMsgFile(e.target.files?.[0] || null); e.target.value = ''; }} />
                      <button onClick={() => msgFileRef.current?.click()}
                        className="h-9 w-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-orange-500 hover:bg-orange-50 transition-colors flex-shrink-0">
                        <Paperclip className="h-4 w-4" />
                      </button>
                      <input data-testid="contractor-msg-input" type="text" value={msgText}
                        onChange={e => setMsgText(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                        className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:bg-white transition-all"
                        placeholder={tr ? 'Admin ile yazışın...' : 'Chat with admin...'} />
                      <button data-testid="contractor-send-msg" onClick={sendMessage} disabled={msgLoading}
                        className="h-9 w-9 flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white rounded-xl flex-shrink-0 transition-colors shadow-sm disabled:opacity-50">
                        {msgLoading
                          ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          : <Send className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>

              </div>{/* end 2-col grid */}
            </div>
          )}

          {/* ── Bid Success Modal ── */}
          {bidSuccessModal.open && bidSuccessModal.bid && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
              <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Top gradient */}
                <div className="h-2 bg-gradient-to-r from-orange-400 to-red-500" />
                <div className="px-8 py-8 text-center">
                  {/* Success icon */}
                  <div className="mx-auto mb-5 h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="h-9 w-9 text-emerald-500" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">
                    {tr ? 'Teklifiniz İletildi!' : 'Bid Submitted!'}
                  </h2>
                  <p className="text-sm text-gray-500 mb-6">
                    {tr ? 'Teklifiniz başarıyla gönderildi ve değerlendirmeye alındı.' : 'Your bid has been submitted and is under review.'}
                  </p>
                  {/* Bid summary */}
                  <div className="bg-gray-50 rounded-2xl p-4 text-left space-y-2 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-400">{tr ? 'Teklif Tutarı' : 'Bid Amount'}</span>
                      <span className="font-bold text-orange-600 text-base">
                        {Number(bidSuccessModal.bid.amount).toLocaleString('tr-TR')} {(bidSuccessModal.bid.currency || 'USD').toUpperCase()}
                      </span>
                    </div>
                    {bidSuccessModal.bid.validity_value && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-400">{tr ? 'Geçerlilik' : 'Validity'}</span>
                        <span className="text-sm text-gray-700">{bidSuccessModal.bid.validity_value} {bidSuccessModal.bid.validity_unit || 'gün'}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-400">{tr ? 'Gönderim' : 'Submitted'}</span>
                      <span className="text-sm text-gray-700">
                        {new Date(bidSuccessModal.bid.created_at).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                  <Button data-testid="bid-success-ok" onClick={() => {
                    setBidSuccessModal({ open: false, bid: null });
                    // Reset form for next bid
                    setBidForm({ amount: '', currency: 'usd', validity_value: '', validity_unit: 'gün', notes: '', accept_payment_terms: false, accept_work_conditions: false, accept_safety_rules: false });
                  }}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-2xl py-3 text-base font-semibold">
                    {tr ? 'Tamam' : 'OK'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ─── PROJECTS — Only finalized direct offers (awarded work) ───
              Phase 86AZ.55 — Cards land here only after the admin presses
              "Award & Assign" on the Get Price tab. */}
          {activeSection === 'direct-offers' && (() => {
            const awarded = directOffers.filter(o => o.status === 'finalized');
            return (
              <div className="space-y-4">
                {awarded.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                    <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{tr ? 'Henüz size atanmış bir proje yok.' : 'No projects yet.'}</p>
                    <p className="text-[11px] text-gray-400 mt-1">
                      {tr ? 'Onaylanan teklifler buraya gelir. Aktif talepler için Talepler sekmesine bakın.'
                          : 'Awarded offers appear here. Check the Requests tab for active ones.'}
                    </p>
                  </div>
                ) : awarded.map(o => (
                  <DirectOfferCard key={o.id} offer={o} tr={tr} onResponded={fetchDirectOffers} />
                ))}
              </div>
            );
          })()}

          {/* Phase 86AZ.57 — ISG (Occupational Health & Safety) module */}
          {activeSection === 'isg' && <ContractorISG tr={tr} />}

          {/* ─── BIDS LIST ─── */}
          {activeSection === 'bids' && (
            <div className="space-y-4">
              {bids.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">{tr ? 'Henüz teklif vermediniz.' : 'No bids submitted yet.'}</p>
                </div>
              ) : bids.map(bid => (
                <div key={bid.id} className="bg-white rounded-2xl border border-gray-100 p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-gray-800">{bid.design?.title || 'Stand Talebi'}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{bid.design?.hall && `Hall: ${bid.design.hall}`}</p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                      bid.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                      bid.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {bid.status === 'approved' ? (tr ? 'Onaylandı' : 'Approved') :
                       bid.status === 'rejected' ? (tr ? 'Reddedildi' : 'Rejected') : (tr ? 'Beklemede' : 'Pending')}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <span className="text-lg font-bold text-orange-600">{bid.amount?.toLocaleString()} {(bid.currency || 'usd').toUpperCase()}</span>
                    {bid.delivery_days && <span className="text-xs text-gray-400">{bid.delivery_days} {tr ? 'gün' : 'days'}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
